import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { FormField, form, schema, submit, validate } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { firstValueFrom } from 'rxjs';
import { DateTimeDto, MceApiService, StripeMeterEventAuditDto } from '../../../core/mceapi.client.generated';

interface MeterEventsPeriodModel {
  startDate: Date;
  startTime: Date;
  endDate: Date;
  endTime: Date;
}

const isValidDate = (value: Date): boolean => value instanceof Date && !Number.isNaN(value.getTime());

const combineDateAndTime = (date: Date, time: Date): Date => {
  const combined = new Date(date);
  combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return combined;
};

const meterEventsPeriodSchema = schema<MeterEventsPeriodModel>((path) => {
  validate(path.startDate, ({ value }) => isValidDate(value())
    ? undefined
    : { kind: 'invalidStartDate', message: 'Enter a valid start date' });
  validate(path.startTime, ({ value }) => isValidDate(value())
    ? undefined
    : { kind: 'invalidStartTime', message: 'Enter a valid start time' });
  validate(path.endDate, ({ value }) => isValidDate(value())
    ? undefined
    : { kind: 'invalidEndDate', message: 'Enter a valid end date' });
  validate(path.endTime, ({ value, valueOf }) => {
    const endTime = value();
    const startDate = valueOf(path.startDate);
    const startTime = valueOf(path.startTime);
    const endDate = valueOf(path.endDate);

    if (![startDate, startTime, endDate, endTime].every(isValidDate)) {
      return { kind: 'invalidEndTime', message: 'Enter a valid end time' };
    }

    return combineDateAndTime(endDate, endTime) > combineDateAndTime(startDate, startTime)
      ? undefined
      : { kind: 'invalidPeriod', message: 'End time must be after start time' };
  });
});

const initialPeriod = (): MeterEventsPeriodModel => {
  const end = new Date();
  end.setSeconds(0, 0);
  const start = new Date(end.getTime() - 24 * 60 * 60 * 1000);

  return {
    startDate: new Date(start),
    startTime: new Date(start),
    endDate: new Date(end),
    endTime: new Date(end),
  };
};

@Component({
  selector: 'app-meter-events',
  imports: [
    DecimalPipe,
    FormField,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatTimepickerModule,
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
  ],
  templateUrl: './meter-events.component.html',
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeterEventsComponent {
  readonly userUid = input.required<string>();

  private readonly mceApiService = inject(MceApiService);
  private readonly dateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  });

  protected readonly periodModel = signal<MeterEventsPeriodModel>(initialPeriod());
  protected readonly periodForm = form<MeterEventsPeriodModel>(this.periodModel, meterEventsPeriodSchema);
  protected readonly meterEvents = signal<StripeMeterEventAuditDto[]>([]);
  protected readonly columns = ['time', 'event', 'action', 'credits', 'identifier'];
  protected readonly totalCredits = computed(() =>
    this.meterEvents().reduce((total, event) => total + (event.credits ?? 0), 0));
  protected readonly hasLoaded = signal(false);
  protected readonly loadError = signal<string | undefined>(undefined);

  protected readonly viewState = computed(() => ({
    events: this.meterEvents(),
    hasLoaded: this.hasLoaded(),
    isLoading: this.periodForm().submitting(),
    loadError: this.loadError(),
  }));

  protected loadEvents(event: Event): void {
    event.preventDefault();

    submit(this.periodForm, async () => {
      const period = this.periodModel();
      const startTime = combineDateAndTime(period.startDate, period.startTime);
      const endTime = combineDateAndTime(period.endDate, period.endTime);

      this.loadError.set(undefined);

      try {
        const events = await firstValueFrom(
          this.mceApiService.v1BillingMeterEventsUser(this.userUid(), startTime, endTime)
        );
        this.meterEvents.set(events);
      } catch (error) {
        console.error('Error loading meter events', error);
        this.meterEvents.set([]);
        this.loadError.set('Unable to load meter events. Please try again.');
      } finally {
        this.hasLoaded.set(true);
      }
    });
  }

  protected formatDateTime(value: DateTimeDto | undefined): string {
    if (!value?.value) return 'Time unavailable';

    const date = new Date(value.value);
    return isValidDate(date) ? this.dateTimeFormatter.format(date) : value.value;
  }

}
